import {
  BOTTOMBAR_HEIGHT,
  getNavigationItems,
  premiumGradientAnimation,
  premiumIconAnimation,
} from '@common/defs/layout';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Any } from '@common/defs/types';
import useAuth from '@modules/auth/hooks/api/useAuth';

interface BottomBarProps {}

const BottomBar = (_props: BottomBarProps) => {
  const [currentTab, setCurrentTab] = useState<string | false>(false);
  const router = useRouter();
  const currentPath = router.pathname;
  const { user } = useAuth();

  const mobileItems = useMemo(
    () =>
      getNavigationItems(user).filter((item) => {
        if (item.hideOnMobile) {
          return false;
        }
        if (item.label === 'Prospecter' && user?.metadata?.prospectingEnabled === false) {
          return false;
        }
        return true;
      }),
    [user?.metadata?.prospectingEnabled, user?.hasActiveSx, user?.isDemo]
  );

  const isPremiumTabSelected =
    currentTab !== false && mobileItems.find((item) => item.href === currentTab)?.premium;

  useEffect(() => {
    const activeItem = mobileItems.find((item) => item.isActive(currentPath));
    if (activeItem) {
      setCurrentTab(activeItem.href);
    } else {
      setCurrentTab(false);
    }
  }, [currentPath, mobileItems]);

  return (
    <Box
      sx={{
        position: 'fixed',
        height: BOTTOMBAR_HEIGHT,
        paddingX: 2,
        paddingY: 0,
        gap: 0,
        zIndex: 1200,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'common.white',

        right: 0,
        left: 0,
        bottom: 0,
        borderRadius: '20px 20px 0 0',
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.18)',
      }}
    >
      <Tabs
        value={currentTab}
        onChange={(event: Any, newValue: string) => {
          if (
            event.ctrlKey || // Ctrl+Clic
            event.metaKey || // Cmd+Clic (Mac)
            (event.button && event.button === 1) // Clic molette
          ) {
            return;
          }

          setCurrentTab(newValue);
        }}
        sx={{
          width: '100%',
          height: '100%',
          '& .MuiTabs-fixed': {
            display: 'flex',
            justifyContent: 'center',
          },
          '& .MuiTabs-list': {
            width: '100%',
          },
          '& .MuiTabs-indicator': isPremiumTabSelected
            ? {
                background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1)',
                backgroundSize: '200% auto',
                animation: `${premiumGradientAnimation} 4s ease infinite`,
                height: 3,
              }
            : {},
        }}
      >
        {mobileItems.map((item) => {
          const isSelected = currentTab === item.href;
          const isPremiumActive = item.premium && isSelected;

          return (
            <Tab
              component={Link}
              href={item.href}
              key={item.href}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <item.icon />
                  <Typography variant="body2" fontSize={13} fontWeight={400}>
                    {item.label}
                  </Typography>
                </Box>
              }
              value={item.href}
              sx={{
                flex: 1,
                ...(isPremiumActive
                  ? {
                      '&.Mui-selected': { color: 'transparent' },
                      '& .MuiSvgIcon-root': {
                        animation: `${premiumIconAnimation} 4s ease infinite`,
                      },
                      '& .MuiTypography-root': {
                        background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1)',
                        backgroundSize: '200% auto',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: `${premiumGradientAnimation} 4s ease infinite`,
                      },
                    }
                  : {}),
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default BottomBar;
